/**
 * Adventure caching using IndexedDB for offline/poor coverage support
 * Stores adventures and search results with automatic expiry
 */

import { AdventureDto, AdventureSearchRequest } from '@api/types/adventure'
import { PaginatedResponse } from '@api/types/common'
import { STORES, get, put, getAll, deleteOlderThan, clearStore, count } from './indexedDB'

const CACHE_EXPIRY_MS = 1000 * 60 * 60 * 24 // 24 hours
const MAX_SEARCH_CACHE_ENTRIES = 100
const MAX_ADVENTURE_CACHE_ENTRIES = 500

interface CachedAdventure {
  id: string
  data: AdventureDto
  lastRefreshed: number
  createdAt: number
}

interface CachedSearchResult {
  cacheKey: string
  data: PaginatedResponse<AdventureDto>
  searchParams: AdventureSearchRequest
  lastRefreshed: number
  createdAt: number
}

/**
 * Generate a stable cache key from search request
 */
function generateCacheKey(request: AdventureSearchRequest): string {
  const keyParts: string[] = []

  if (request.useViewportBounds) {
    // For viewport-based searches, round coordinates to 2 decimals to reduce cache variations
    keyParts.push('vp')
    keyParts.push(request.viewportNorthEastLat?.toFixed(2) || '')
    keyParts.push(request.viewportNorthEastLng?.toFixed(2) || '')
    keyParts.push(request.viewportSouthWestLat?.toFixed(2) || '')
    keyParts.push(request.viewportSouthWestLng?.toFixed(2) || '')
  } else {
    // For radius-based searches
    keyParts.push('radius')
    keyParts.push(request.userLatitude?.toFixed(2) || '')
    keyParts.push(request.userLongitude?.toFixed(2) || '')
    keyParts.push(String(request.radiusMiles || ''))
  }

  // Add other filters
  if (request.minDifficulty) keyParts.push(`d${request.minDifficulty}`)
  // Route length is part of the question, not a display preference: without it the
  // offline fallback would answer a "four miles or longer" search with the cached
  // results of an unfiltered one.
  if (request.minLengthMiles) keyParts.push(`lmin${request.minLengthMiles}`)
  if (request.maxLengthMiles) keyParts.push(`lmax${request.maxLengthMiles}`)
  if (request.tags?.length) keyParts.push(`t${request.tags.sort().join(',')}`)
  if (request.searchTerm) keyParts.push(`s${request.searchTerm}`)
  if (request.sortBy) keyParts.push(`sb${request.sortBy}`)
  if (request.sortDirection) keyParts.push(`sd${request.sortDirection}`)
  if (request.page) keyParts.push(`p${request.page}`)

  return keyParts.filter(Boolean).join('_')
}

/**
 * Store an individual adventure
 */
export async function cacheAdventure(adventure: AdventureDto): Promise<void> {
  try {
    const cached: CachedAdventure = {
      id: adventure.id,
      data: adventure,
      lastRefreshed: Date.now(),
      createdAt: Date.now(),
    }

    await put(STORES.ADVENTURES, cached)

    // Cleanup if we have too many adventures
    const adventureCount = await count(STORES.ADVENTURES)
    if (adventureCount > MAX_ADVENTURE_CACHE_ENTRIES) {
      const cutoffTime = Date.now() - CACHE_EXPIRY_MS
      await deleteOlderThan(STORES.ADVENTURES, cutoffTime)
    }
  } catch (error) {
    console.warn('Failed to cache adventure:', error)
  }
}

/**
 * Get a cached adventure by ID
 */
export async function getCachedAdventure(id: string): Promise<AdventureDto | null> {
  try {
    const cached = await get<CachedAdventure>(STORES.ADVENTURES, id)

    if (!cached) return null

    // Check if expired
    const age = Date.now() - cached.lastRefreshed
    if (age > CACHE_EXPIRY_MS) {
      console.log(`⏰ Cached adventure ${id} expired (${Math.round(age / 1000 / 60)}min old)`)
      return null
    }

    console.log(`🗄️ Retrieved adventure ${id} from cache`)
    return cached.data
  } catch (error) {
    console.warn('Failed to get cached adventure:', error)
    return null
  }
}

/**
 * Cache search results
 */
export async function cacheSearchResults(
  request: AdventureSearchRequest,
  data: PaginatedResponse<AdventureDto>
): Promise<void> {
  try {
    const cacheKey = generateCacheKey(request)
    const now = Date.now()

    const cached: CachedSearchResult = {
      cacheKey,
      data,
      searchParams: request,
      lastRefreshed: now,
      createdAt: now,
    }

    await put(STORES.SEARCH_RESULTS, cached)

    // Also cache individual adventures from the results
    if (data.items && Array.isArray(data.items)) {
      await Promise.all(
        data.items.map((adventure) => cacheAdventure(adventure))
      )
    }

    console.log(`💾 Cached ${data.items?.length || 0} adventures for search: ${cacheKey.substring(0, 50)}...`)

    // Cleanup if we have too many search results
    const searchCount = await count(STORES.SEARCH_RESULTS)
    if (searchCount > MAX_SEARCH_CACHE_ENTRIES) {
      const cutoffTime = now - CACHE_EXPIRY_MS
      const deleted = await deleteOlderThan(STORES.SEARCH_RESULTS, cutoffTime)
      if (deleted > 0) {
        console.log(`🗑️ Cleaned up ${deleted} old search results`)
      }
    }
  } catch (error) {
    console.warn('Failed to cache search results:', error)
  }
}

/**
 * Get adventures within viewport bounds from cache
 */
async function getAdventuresInViewport(
  northEastLat: number,
  northEastLng: number,
  southWestLat: number,
  southWestLng: number
): Promise<AdventureDto[]> {
  try {
    const allAdventures = await getAll<CachedAdventure>(STORES.ADVENTURES)
    const now = Date.now()

    // Filter adventures that are:
    // 1. Not expired
    // 2. Within viewport bounds
    const adventuresInViewport = allAdventures
      .filter(cached => {
        // Check expiry
        if (now - cached.lastRefreshed > CACHE_EXPIRY_MS) {
          return false
        }

        // Check if adventure has location
        const adventure = cached.data
        if (!adventure.location?.latitude || !adventure.location?.longitude) {
          return false
        }

        // Check if within bounds
        const lat = adventure.location.latitude
        const lng = adventure.location.longitude

        return (
          lat <= northEastLat &&
          lat >= southWestLat &&
          lng <= northEastLng &&
          lng >= southWestLng
        )
      })
      .map(cached => cached.data)

    return adventuresInViewport
  } catch (error) {
    console.warn('Failed to get adventures in viewport:', error)
    return []
  }
}

/**
 * Get cached search results
 */
export async function getCachedSearchResults(
  request: AdventureSearchRequest
): Promise<PaginatedResponse<AdventureDto> | null> {
  try {
    const cacheKey = generateCacheKey(request)
    const cached = await get<CachedSearchResult>(STORES.SEARCH_RESULTS, cacheKey)

    // Try exact cache key match first
    if (cached) {
      const age = Date.now() - cached.lastRefreshed
      if (age <= CACHE_EXPIRY_MS) {
        console.log(`🗄️ Using cached search results (${cached.data.items?.length || 0} adventures, ${Math.round(age / 1000 / 60)}min old)`)
        return cached.data
      } else {
        console.log(`⏰ Cached search expired (${Math.round(age / 1000 / 60)}min old)`)
      }
    }

    // If no exact match and we have viewport bounds, try to build result from individual cached adventures
    if (request.useViewportBounds &&
        request.viewportNorthEastLat !== undefined &&
        request.viewportNorthEastLng !== undefined &&
        request.viewportSouthWestLat !== undefined &&
        request.viewportSouthWestLng !== undefined) {

      const adventuresInViewport = await getAdventuresInViewport(
        request.viewportNorthEastLat,
        request.viewportNorthEastLng,
        request.viewportSouthWestLat,
        request.viewportSouthWestLng
      )

      if (adventuresInViewport.length > 0) {
        console.log(`🗺️ Built search result from ${adventuresInViewport.length} cached adventures in viewport`)

        // Build a paginated response
        const pageSize = request.pageSize || 20
        const page = request.page || 1
        const totalCount = adventuresInViewport.length
        const totalPages = Math.ceil(totalCount / pageSize)
        const startIndex = (page - 1) * pageSize
        const endIndex = startIndex + pageSize

        return {
          items: adventuresInViewport.slice(startIndex, endIndex),
          pageNumber: page,
          totalCount,
          pageSize,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        }
      }
    }

    console.log(`❌ No cache found for: ${cacheKey.substring(0, 50)}...`)
    return null
  } catch (error) {
    console.warn('Failed to get cached search results:', error)
    return null
  }
}

/**
 * Clear all adventure caches
 */
export async function clearAdventureCache(): Promise<void> {
  try {
    await clearStore(STORES.ADVENTURES)
    await clearStore(STORES.SEARCH_RESULTS)
    console.log('🗑️ All adventure caches cleared')
  } catch (error) {
    console.warn('Failed to clear adventure cache:', error)
  }
}

/**
 * Clean up expired entries
 */
export async function cleanupExpiredCache(): Promise<void> {
  try {
    const cutoffTime = Date.now() - CACHE_EXPIRY_MS

    const deletedAdventures = await deleteOlderThan(STORES.ADVENTURES, cutoffTime)
    const deletedSearches = await deleteOlderThan(STORES.SEARCH_RESULTS, cutoffTime)

    const total = deletedAdventures + deletedSearches
    if (total > 0) {
      console.log(`🗑️ Cleaned up ${deletedAdventures} adventures and ${deletedSearches} search results`)
    }
  } catch (error) {
    console.warn('Failed to cleanup expired cache:', error)
  }
}

/**
 * Get cache statistics
 */
export async function getCacheStats(): Promise<{
  adventureCount: number
  searchResultCount: number
  estimatedSize: string
}> {
  try {
    const adventureCount = await count(STORES.ADVENTURES)
    const searchResultCount = await count(STORES.SEARCH_RESULTS)

    // Estimate size (rough calculation)
    const adventures = await getAll<CachedAdventure>(STORES.ADVENTURES)
    const searches = await getAll<CachedSearchResult>(STORES.SEARCH_RESULTS)

    const adventuresSize = JSON.stringify(adventures).length
    const searchesSize = JSON.stringify(searches).length
    const totalBytes = adventuresSize + searchesSize

    let estimatedSize = `${totalBytes} bytes`
    if (totalBytes > 1024 * 1024) {
      estimatedSize = `${(totalBytes / 1024 / 1024).toFixed(2)} MB`
    } else if (totalBytes > 1024) {
      estimatedSize = `${(totalBytes / 1024).toFixed(2)} KB`
    }

    return {
      adventureCount,
      searchResultCount,
      estimatedSize,
    }
  } catch (error) {
    console.warn('Failed to get cache stats:', error)
    return {
      adventureCount: 0,
      searchResultCount: 0,
      estimatedSize: '0 bytes',
    }
  }
}

/**
 * Initialize cache (run cleanup on app start)
 */
export async function initializeCache(): Promise<void> {
  try {
    await cleanupExpiredCache()
    const stats = await getCacheStats()
    console.log(`📊 Cache initialized: ${stats.adventureCount} adventures, ${stats.searchResultCount} searches (${stats.estimatedSize})`)
  } catch (error) {
    console.warn('Failed to initialize cache:', error)
  }
}
